import styled, { useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import { CaretDownOutlined } from "@ant-design/icons";
import Dropdown from "common/Dropdown/Dropdown.tsx";
import React, { useContext, useMemo, useState } from "react";
import { ItemType } from "antd/es/menu/interface";
import { openMessage } from "utils/misc.tsx";
import { isSymbol } from "@shared/types/utils.ts";
import Checkbox from "common/Checkbox/Checkbox.tsx";
import { Tag } from "features/bots/BotForm/common/styles.ts";
import { useExchangesMetadata } from "@shared/utils/hooks/misc.ts";
import { sortByStringProperty } from "@shared/utils/general.ts";
import { ClientData } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { unionBy } from "lodash";
import {
  BOT_FORM_EXCHANGES_DROPDOWN_ID,
  getBotFormExchangeDropdownOptionId,
} from "testIds.ts";

interface ExchangesTagDropdownProps {
  getClientErrorMessage: (client: ClientData) => string | undefined;
  selectedAccountIds: Set<number> | undefined;
  validClients?: ClientData[];
}

const ExchangesTagDropdown = ({
  getClientErrorMessage,
  selectedAccountIds,
  validClients,
}: ExchangesTagDropdownProps) => {
  const { setAccounts, accounts } = useContext(BotFormContext)!;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { exchanges: allExchanges } = useExchangesMetadata();
  const { isNewLook } = useTheme();

  const options = useMemo(() => {
    return allExchanges
      .map((exchange) => {
        const allExchangeClients =
          validClients?.filter((client) => client.exchange === exchange) || [];
        const validExchangeClients = allExchangeClients.filter(
          (client) => !getClientErrorMessage(client)
        );
        const errorMessage = allExchangeClients
          .map((client) => getClientErrorMessage(client))
          .find((message) => !!message);
        const numValid = validExchangeClients.length;
        const numSelected = validExchangeClients.reduce(
          (acc, c) => acc + Number(!!selectedAccountIds?.has(c.client_id)),
          0
        );
        const isAllSelected = numValid > 0 && numSelected === numValid;
        const noneSelected = numSelected === 0;
        const isIndeterminate = !isAllSelected && !noneSelected;
        return {
          exchange,
          numTotal: allExchangeClients.length,
          numValid,
          numSelected,
          isAllSelected,
          isIndeterminate,
          validExchangeClients,
          allExchangeClients,
          errorMessage,
        };
      })
      .sort((a, b) => {
        return (
          Number(!!b.validExchangeClients.length) -
            Number(!!a.validExchangeClients.length) ||
          sortByStringProperty(a.exchange, b.exchange)
        );
      });
  }, [allExchanges, getClientErrorMessage, selectedAccountIds, validClients]);

  const menuItems: ItemType[] = useMemo(
    () =>
      options.map((row) => ({
        key: row.exchange,
        className: row.numValid === 0 ? "invalid" : undefined,
        onClick: () => {
          if (row.numTotal === 0 || row.numValid === 0) {
            openMessage(
              row.errorMessage ??
                `You don't have any ${row.exchange} accounts.`,
              "warning"
            );
            return;
          }
          toggleExchangeClients(
            row.validExchangeClients,
            isSymbol(accounts) ? [] : accounts,
            setAccounts
          );
        },
        label: (
          <OptionRow
            data-testid={getBotFormExchangeDropdownOptionId(row.exchange)}
          >
            <CheckboxWrapper>
              <Checkbox
                checked={row.isAllSelected}
                indeterminate={row.isIndeterminate}
                disabled={row.numValid === 0}
                $useDisabledStyles={row.numValid === 0}
              />
              <ExchangeName>{row.exchange}</ExchangeName>
            </CheckboxWrapper>
            <Count>
              {row.numSelected}/{row.numTotal}
            </Count>
          </OptionRow>
        ),
      })),
    [options, accounts, setAccounts]
  );

  return (
    <Dropdown
      title="Exchanges"
      open={isDropdownOpen}
      onOpenChange={(open, info) => {
        if (info?.source === "menu") return;
        setIsDropdownOpen(open);
      }}
      mobileCloseOnSelect={false}
      trigger={["click"]}
      menu={{
        items: menuItems,
        style: {
          maxHeight: 300,
          backgroundClip: "border-box",
        },
      }}
    >
      <Tag data-testid={BOT_FORM_EXCHANGES_DROPDOWN_ID}>
        Exchanges{" "}
        {isNewLook ? (
          <NewLookCaret size={14} strokeWidth={1.75} $open={isDropdownOpen} />
        ) : (
          <Caret $open={isDropdownOpen}>▾</Caret>
        )}
      </Tag>
    </Dropdown>
  );
};

export default ExchangesTagDropdown;

export const toggleExchangeClients = (
  toggleClients: ClientData[],
  selectedClients: ClientData[] | undefined,
  setAccounts: (accounts: ClientData[]) => void
) => {
  const toggleClientsIds = new Set(toggleClients.map((c) => c.client_id));
  const selectedIds = new Set(selectedClients?.map((c) => c.client_id));
  const isAllSelected = toggleClients.every((c) =>
    selectedIds.has(c.client_id)
  );

  let nextSelected: ClientData[] | undefined;
  if (isAllSelected) {
    nextSelected = selectedClients?.filter(
      (client) => !toggleClientsIds.has(client.client_id)
    );
  } else {
    nextSelected = unionBy(selectedClients, toggleClients, "client_id");
  }
  setAccounts(nextSelected || []);
};

const NewLookCaret = styled(ChevronDown)<{ $open?: boolean }>`
  margin-left: 4px;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  transition: transform 120ms ease-in-out;
`;
const Caret = styled(CaretDownOutlined)<{ $open?: boolean }>`
  margin-left: 6px;
  display: inline-block;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  transition: transform 120ms ease-in-out;
`;
const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
const CheckboxWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const ExchangeName = styled.span`
  font-weight: 500;
`;
const Count = styled.span`
  opacity: 0.8;
  font-size: 12px;
`;
